type FontSizeKey = 'default' | 'large' | 'xLarge';

const typographySettings = (fontSize?: FontSizeKey | string) => {
  const fontSizeMultiplier = {
    default: 1,
    large: 1.3,
    xLarge: 1.7
  }[fontSize || 'default'] ?? 1;

  const lineHeightMultiplier = {
    default: 1,
    large: 1.2,
    xLarge: 1.5
  }[fontSize || 'default'] ?? 1;

  return {
    he: {
      default: {
        h1: {
          fontFamily: 'SimplerPro',
          fontSize: '3rem',
          fontWeight: 900,
          lineHeight: '3rem'
        },
        h2: {
          fontFamily: 'SimplerPro',
          fontSize: '1.5rem',
          fontWeight: 600,
        },
        body1: {
          fontFamily: 'Arial, sans-serif',
          fontSize: `${1.6 * fontSizeMultiplier}rem`,
          fontWeight: 400,
          lineHeight: `${2.4 * lineHeightMultiplier}rem`,
        },
        body2: {
          fontFamily: 'Fedra Sans AHL',
          fontSize: '2.2rem',
          fontWeight: 400,
          lineHeight: '2.1rem',
          letterSpacing: '-0.32px'
        },
        p: {
          fontSize: `${1.6 * fontSizeMultiplier}rem`,
          letterSpacing: '-0.32px',
          lineHeight: `${2.3 * lineHeightMultiplier}rem`,
          fontFamily: 'SimplerPro',
          fontWeight: 400,
        },
        button: {
          fontSize: '1.6rem',
          fontFamily: 'SimplerPro',
          fontWeight: '400',
          lineHeight: '1.5rem',
          letterSpacing: '-0.32px',
          whiteSpace: 'pre-line'
        },
        caption: {
          fontFamily: 'SimplerPro',
          fontWeight: '400',
          fontSize: '1.8rem',
          lineHeight: '3.5rem',
          letterSpacing: '-0.32px'
        }
      }
    },
    en: {
      default: {
        h1: {
          fontSize: '3.2rem',
          fontFamily: 'Roboto Flex',
          fontWeight: '700',
          lineHeight: '3rem',
          letterSpacing: '-0.32px'
        },
        h2: {
          fontSize: `${2 * fontSizeMultiplier}rem`,
          lineHeight: `${2 * lineHeightMultiplier}rem`,
          letterSpacing: '-0.32px'
        },
        p: {
          fontSize: `${1.6 * fontSizeMultiplier}rem`,
          fontFamily: 'Roboto Flex',
          fontWeight: '400',
          lineHeight: `${2.4 * lineHeightMultiplier}rem`,
          letterSpacing: '-0.32px'
        },
        body1: {
          fontFamily: 'Roboto Flex',
          fontSize: `${1.6 * fontSizeMultiplier}rem`,
          fontWeight: '400',
          lineHeight: `${2.4 * lineHeightMultiplier}rem`,
          letterSpacing: '-0.32px'
        },
        button: {
          fontSize: '1.4rem',
          fontFamily: 'SimplerPro',
          fontWeight: '400',
          lineHeight: '2rem',
          letterSpacing: '-0.32px',
          whiteSpace: 'pre-line',
          textTransform: 'none'
        },
        body2: {
          fontFamily: 'Roboto Flex',
          fontWeight: 400,
          fontSize: '2.4rem',
          letterSpacing: '-0.32px',
          lineHeight: '2.3rem'
        },
        tour1: {
          fontFamily: 'Poppins',
          fontWeight: 500,
          fontSize: `${1.4 * fontSizeMultiplier}rem`,
          letterSpacing: '-0.32px',
          lineHeight: `${2 * lineHeightMultiplier}rem`
        },
        tour2: {
          fontFamily: 'Poppins',
          fontWeight: 100,
          fontSize: `${1.2 * fontSizeMultiplier}rem`,
          letterSpacing: '-0.32px',
          lineHeight: `${2 * lineHeightMultiplier}rem`
        }
      }
    },
    ar: {
      default: {
        h1: {
          fontFamily: 'Noto Sans Arabic, sans-serif', // Noto Sans Arabic from Google Fonts
          fontSize: '3.5rem', // Adjust the size if needed
          fontWeight: 700,
          lineHeight: '3.3rem',
          letterSpacing: '-0.32px'
        },
        h2: {
          fontFamily: 'Noto Sans Arabic, sans-serif',
          fontSize: '2.6rem',
          fontWeight: 600,
          lineHeight: '3rem'
        },
        body1: {
          fontFamily: 'Noto Sans Arabic, sans-serif',
          fontSize: '1.8rem',
          fontWeight: 400,
          lineHeight: '2.5rem',
          letterSpacing: '-0.32px'
        },
        body2: {
          fontFamily: 'Noto Sans Arabic, sans-serif',
          fontSize: '1.6rem',
          fontWeight: 400,
          lineHeight: '2.3rem',
          letterSpacing: '-0.32px'
        },
        p: {
          fontFamily: 'Noto Sans Arabic, sans-serif',
          fontSize: `${1.6 * fontSizeMultiplier}rem`,
          fontWeight: 400,
          lineHeight: `${2.5 * lineHeightMultiplier}rem`,
          letterSpacing: '-0.32px'
        },
        button: {
          fontFamily: 'Noto Sans Arabic, sans-serif',
          fontSize: '1.8rem',
          fontWeight: 400,
          lineHeight: '2.2rem',
          letterSpacing: '-0.32px',
          textTransform: 'none' // Keep text-transform consistent
        },
        caption: {
          fontFamily: 'Noto Sans Arabic, sans-serif',
          fontSize: '1.4rem',
          fontWeight: 400,
          lineHeight: '2rem',
          letterSpacing: '-0.32px'
        }
      }
    },
    nl: {
      default: {
        h1: {
          fontSize: '3.2rem',
          fontFamily: 'Roboto Flex',
          fontWeight: '700',
          lineHeight: '3rem',
          letterSpacing: '-0.32px'
        },
        h2: {
          fontSize: `${2 * fontSizeMultiplier}rem`,
          lineHeight: `${2 * lineHeightMultiplier}rem`,
          letterSpacing: '-0.32px'
        },
        p: {
          fontSize: `${1.6 * fontSizeMultiplier}rem`,
          fontFamily: 'Roboto Flex',
          fontWeight: '400',
          lineHeight: `${2.4 * lineHeightMultiplier}rem`,
          letterSpacing: '-0.32px'
        },
        body1: {
          fontFamily: 'Roboto Flex',
          fontSize: `${1.6 * fontSizeMultiplier}rem`,
          fontWeight: '400',
          lineHeight: `${2.4 * lineHeightMultiplier}rem`,
          letterSpacing: '-0.32px'
        },
        button: {
          fontSize: '1.4rem',
          fontFamily: 'SimplerPro',
          fontWeight: '400',
          lineHeight: '2rem',
          letterSpacing: '-0.32px',
          whiteSpace: 'pre-line',
          textTransform: 'none'
        },
        body2: {
          fontFamily: 'Roboto Flex',
          fontWeight: 400,
          fontSize: '2.4rem',
          letterSpacing: '-0.32px',
          lineHeight: '2.3rem'
        },
        tour1: {
          fontFamily: 'Poppins',
          fontWeight: 500,
          fontSize: `${1.4 * fontSizeMultiplier}rem`,
          letterSpacing: '-0.32px',
          lineHeight: `${2 * lineHeightMultiplier}rem`
        },
        tour2: {
          fontFamily: 'Poppins',
          fontWeight: 100,
          fontSize: `${1.2 * fontSizeMultiplier}rem`,
          letterSpacing: '-0.32px',
          lineHeight: `${2 * lineHeightMultiplier}rem`
        }
      }
    }
  };
};

export default typographySettings;
